import React, { useEffect, useState, useMemo } from "react";
import api from "../utils/axiosConfig";
import NavMenu from "./Menu";
import {
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Typography,
  CircularProgress,
  Modal,
  Fade,
  Backdrop,
  Snackbar,
  Alert,
  Paper,
  MenuItem,
  Select,
  FormControl,
  Divider,
  Avatar,
  TextField,
  Fab,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SortIcon from "@mui/icons-material/Sort";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import AddIcon from "@mui/icons-material/Add";

const ManagePets = () => {
  const [pets, setPets] = useState([]);
  const [selectedPet, setSelectedPet] = useState(null);
  const [images, setImages] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOption, setSortOption] = useState("name-asc");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  // Modal states
  const [openNewPet, setOpenNewPet] = useState(false);
  const [newPetName, setNewPetName] = useState("");
  const [newPetFile, setNewPetFile] = useState(null);
  const [newPetPreview, setNewPetPreview] = useState(null);

  const baseURL = api.defaults.baseURL || "";

  const handleSnackbar = (msg, type = "info") => setSnackbar({ open: true, message: msg, severity: type });
  const closeSnackbar = () => setSnackbar({ ...snackbar, open: false });

  // Normalize URL
  const normalizeUrl = (url) => {
    if (!url) return "/defaultdog.jpg";
    if (url.startsWith("http")) return url;
    const cleanPath = url.replace(/^public[\\/]/, "");
    return `${baseURL}/${cleanPath}`;
  };

  // -------------------- FETCH PETS --------------------
  useEffect(() => {
    fetchPets();
  }, []);

  const fetchPets = async () => {
    try {
      const res = await api.get("/api/getpets");
      const list = Array.isArray(res.data) ? res.data : [];
      setPets(list);
      if (!selectedPet && list.length > 0) {
        setSelectedPet(list[0]);
        fetchImages(list[0].name);
      }
    } catch (err) {
      console.error("Error fetching pets:", err);
      handleSnackbar("Failed to load pets", "error");
    }
  };

  const fetchImages = async (petName) => {
    try {
      const res = await api.get(`/api/getimages?name=${encodeURIComponent(petName)}`);
      setImages(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching images:", err);
      handleSnackbar("Failed to load images", "error");
    }
  };

  const handleSelectPet = (pet) => {
    setSelectedPet(pet);
    fetchImages(pet.name);
  };

  // -------------------- ADD NEW PET --------------------
  const handleAddPet = async () => {
    if (!newPetName.trim()) return handleSnackbar("Please enter a pet name", "warning");
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("petname", newPetName.trim());
      if (newPetFile) formData.append("files", newPetFile);
      await api.post("/api/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      handleSnackbar("✅ Pet added successfully!", "success");
      setNewPetName("");
      setNewPetFile(null);
      setNewPetPreview(null);
      setOpenNewPet(false);
      fetchPets();
    } catch (err) {
      console.error("Error adding pet:", err);
      handleSnackbar("Failed to add pet", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleNewPetFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setNewPetFile(file);
    const previewUrl = URL.createObjectURL(file);
    setNewPetPreview(previewUrl);
  };

  // -------------------- IMAGE + PET MANAGEMENT --------------------
  const handleAddImage = async (file) => {
    if (!file || !selectedPet) return handleSnackbar("Select a pet first", "warning");
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("files", file);
      formData.append("petname", selectedPet.name);
      await api.post("/api/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      handleSnackbar("✅ Image uploaded successfully!", "success");
      fetchImages(selectedPet.name);
    } catch (err) {
      console.error("Error uploading image:", err);
      handleSnackbar("Failed to upload image", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleReplaceImage = async (imageName, file) => {
    if (!file || !selectedPet) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("files", file);
      formData.append("petname", selectedPet.name);
      await api.post("/api/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      handleSnackbar("✅ Image replaced successfully!", "success");
      fetchImages(selectedPet.name);
    } catch (err) {
      console.error("Error replacing image:", err);
      handleSnackbar("Failed to replace image", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteImage = async (imageName) => {
    if (!window.confirm(`Delete image "${imageName}"?`)) return;
    try {
      await api.delete(`/api/deleteimage?name=${encodeURIComponent(imageName)}`);
      setImages(images.filter((img) => img.Name !== imageName));
      handleSnackbar("Image deleted successfully", "success");
    } catch (err) {
      console.error("Error deleting image:", err);
      handleSnackbar("Failed to delete image", "error");
    }
  };

  const handleDeletePet = async (petName) => {
    if (!window.confirm(`Are you sure you want to delete ${petName}?`)) return;
    try {
      await api.delete(`/api/deletepet?name=${encodeURIComponent(petName)}`);
      handleSnackbar(`Deleted pet: ${petName}`, "success");
      setSelectedPet(null);
      fetchPets();
    } catch (err) {
      console.error("Error deleting pet:", err);
      handleSnackbar("Failed to delete pet", "error");
    }
  };

  // -------------------- FILTER + SORT --------------------
  const filteredPets = useMemo(() => {
    let filtered = pets;
    if (searchTerm.trim()) {
      filtered = filtered.filter((p) => p.name.toLowerCase().includes(searchTerm.toLowerCase()));
    }
    return sortOption === "name-desc"
      ? [...filtered].sort((a, b) => b.name.localeCompare(a.name))
      : [...filtered].sort((a, b) => a.name.localeCompare(b.name));
  }, [pets, searchTerm, sortOption]);

  // -------------------- UI --------------------
  return (
    <Box sx={{ background: "linear-gradient(135deg,#0D47A1,#7B1FA2)", minHeight: "100vh", color: "white" }}>
      <NavMenu />
      <Box sx={{ p: 4, display: "flex", flexDirection: "column", gap: 6 }}>
        <Typography variant="h4" align="center" sx={{ fontWeight: "bold", mb: 2 }}>
          🐾 Manage Pets Dashboard
        </Typography>

        {/* SELECTED PET INFO */}
        {selectedPet && (
          <Paper
            sx={{
              position: "sticky",
              top: 70,
              zIndex: 5,
              p: 3,
              display: "flex",
              alignItems: "center",
              gap: 3,
              borderRadius: 3,
              backdropFilter: "blur(10px)",
              background: "rgba(255,255,255,0.2)",
              border: "1px solid rgba(255,255,255,0.3)",
            }}
          >
            <Avatar
              src={normalizeUrl(selectedPet.image)}
              alt={selectedPet.name}
              sx={{ width: 80, height: 80, border: "2px solid white" }}
            />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                {selectedPet.name}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                📸 {images.length} Images
              </Typography>
            </Box>
            <Box sx={{ ml: "auto", display: "flex", gap: 2 }}>
              <FormControl
                size="small"
                sx={{ minWidth: 180, background: "rgba(255,255,255,0.15)", borderRadius: 2 }}
              >
                <Select
                  value={selectedPet?.name || ""}
                  displayEmpty
                  onChange={(e) => {
                    const pet = pets.find((p) => p.name === e.target.value);
                    if (pet) handleSelectPet(pet);
                  }}
                >
                  <MenuItem value="">
                    <em>Select Pet</em>
                  </MenuItem>
                  {pets.map((p) => (
                    <MenuItem key={p.name} value={p.name}>
                      {p.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="outlined"
                color="error"
                onClick={() => handleDeletePet(selectedPet.name)}
                sx={{ textTransform: "none", borderColor: "rgba(255,255,255,0.6)", color: "#fff" }}
              >
                Delete
              </Button>
            </Box>
          </Paper>
        )}

        {/* PET MANAGEMENT SECTION */}
        <Paper sx={{ p: 4, borderRadius: 4, background: "rgba(255,255,255,0.1)" }}>
          <Typography variant="h5" sx={{ mb: 3 }}>
            🐶 Pet Management
          </Typography>
          <Divider sx={{ mb: 3, borderColor: "rgba(255,255,255,0.3)" }} />

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <TextField
              variant="outlined"
              placeholder="Search pets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: "#4FC3F7", mr: 1 }} />,
              }}
              sx={{ flex: 1, background: "rgba(255,255,255,0.15)", borderRadius: 3 }}
            />
            <FormControl size="small" sx={{ minWidth: 150, background: "rgba(255,255,255,0.15)", borderRadius: 3 }}>
              <Select
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
                startAdornment={<SortIcon sx={{ color: "#4FC3F7", ml: 1 }} />}
              >
                <MenuItem value="name-asc">A → Z</MenuItem>
                <MenuItem value="name-desc">Z → A</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Grid container spacing={3} sx={{ mt: 3 }}>
            {filteredPets.map((pet) => (
              <Grid item key={pet.name} xs={12} sm={6} md={3}>
                <Card
                  onClick={() => handleSelectPet(pet)}
                  sx={{
                    borderRadius: 3,
                    background: selectedPet?.name === pet.name ? "rgba(255,255,255,0.3)" : "rgba(255,255,255,0.1)",
                    cursor: "pointer",
                    "&:hover": { transform: "scale(1.03)" },
                  }}
                >
                  <CardMedia component="img" height="160" image={normalizeUrl(pet.image)} alt={pet.name} />
                  <CardContent>
                    <Typography align="center" variant="h6">
                      {pet.name}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* IMAGE MANAGEMENT */}
        {selectedPet && (
          <Paper sx={{ p: 4, borderRadius: 4, background: "rgba(255,255,255,0.1)" }}>
            <Typography variant="h5" sx={{ mb: 3 }}>
              🖼️ Image Management
            </Typography>
            <Divider sx={{ mb: 3, borderColor: "rgba(255,255,255,0.3)" }} />

            <Paper
              sx={{
                p: 3,
                mx: "auto",
                mb: 4,
                maxWidth: 500,
                border: "2px dashed #fff",
                borderRadius: 3,
                background: "rgba(255,255,255,0.1)",
                textAlign: "center",
                cursor: "pointer",
              }}
            >
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => handleAddImage(e.target.files[0])}
                id="upload-img"
              />
              <label htmlFor="upload-img" style={{ cursor: "pointer" }}>
                <AddPhotoAlternateIcon sx={{ fontSize: 50, color: "#4FC3F7", mb: 1 }} />
                <Typography>Click to add new image</Typography>
              </label>
            </Paper>

            <Grid container spacing={3}>
              {images.map((img) => (
                <Grid item key={img.Name} xs={12} sm={6} md={3}>
                  <Card sx={{ background: "rgba(0,0,0,0.4)" }}>
                    <CardMedia
                      component="img"
                      height="180"
                      image={normalizeUrl(img.Location)}
                      onClick={() => setPreviewImage(normalizeUrl(img.Location))}
                    />
                    <CardActions sx={{ justifyContent: "space-between" }}>
                      <Button color="error" onClick={() => handleDeleteImage(img.Name)}>
                        Delete
                      </Button>
                      <Button variant="contained" component="label" color="secondary">
                        Replace
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={(e) => handleReplaceImage(img.Name, e.target.files[0])}
                        />
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}

        {/* ➕ Floating Add Button */}
        <Fab
          color="primary"
          aria-label="add"
          onClick={() => setOpenNewPet(true)}
          sx={{
            position: "fixed",
            bottom: 40,
            right: 40,
            background: "linear-gradient(135deg,#43A047,#66BB6A)",
            color: "white",
            "&:hover": { background: "linear-gradient(135deg,#2E7D32,#43A047)" },
          }}
        >
          <AddIcon />
        </Fab>

        {/* 🐕 NEW PET MODAL */}
        <Modal open={openNewPet} onClose={() => setOpenNewPet(false)} closeAfterTransition BackdropComponent={Backdrop}>
          <Fade in={openNewPet}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                bgcolor: "background.paper",
                borderRadius: 3,
                p: 4,
                boxShadow: 24,
                minWidth: 400,
                maxWidth: "90vw",
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, textAlign: "center", fontWeight: "bold" }}>
                🐾 Add New Pet
              </Typography>

              {newPetPreview && (
                <Box sx={{ textAlign: "center", mb: 2 }}>
                  <img
                    src={newPetPreview}
                    alt="Preview"
                    style={{ width: "100%", maxHeight: 200, borderRadius: 12, objectFit: "cover" }}
                  />
                </Box>
              )}

              <TextField
                label="Pet Name"
                fullWidth
                variant="outlined"
                value={newPetName}
                onChange={(e) => setNewPetName(e.target.value)}
                sx={{ mb: 3 }}
              />

              <Button
                variant="contained"
                component="label"
                fullWidth
                sx={{
                  mb: 3,
                  background: "linear-gradient(135deg,#1565C0,#42A5F5)",
                  "&:hover": { background: "linear-gradient(135deg,#42A5F5,#1565C0)" },
                }}
              >
                {newPetFile ? "Change Image" : "Upload Pet Image (Optional)"}
                <input type="file" hidden accept="image/*" onChange={handleNewPetFileChange} />
              </Button>

              <Button
                fullWidth
                variant="contained"
                color="success"
                onClick={handleAddPet}
                disabled={loading}
                sx={{ fontWeight: "bold" }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Add Pet"}
              </Button>
            </Box>
          </Fade>
        </Modal>

        {/* SNACKBAR */}
        <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={closeSnackbar}>
          <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default ManagePets;
